/** Müllabfuhr: Hinweis-Banner und Terminliste (offline aus dem Cache). */
import { actionQueue, cachedGet, currentMe, enqueueAction } from '../store.js';
import { overlayPutouts } from '../optimistic.js';
import type { PutoutDTO } from '../../shared/types.js';
import { dayLabel, esc } from '../ui.js';
import { berlinDate, berlinTime, daysBetween } from '../../shared/time.js';
import {
  WASTE_TYPES, joinLabels, upcomingWaste, wasteKey, wasteNotice, type WasteEvent, type WasteType,
} from '../../shared/waste.js';

interface WasteResponse {
  events: WasteEvent[];
  putouts?: PutoutDTO[];
  status: { lastSuccess: number | null };
}

export interface WasteData {
  events: WasteEvent[];
  putouts: PutoutDTO[];
  lastSuccess: number | null;
  offline: boolean;
}

export async function loadWaste(): Promise<WasteData> {
  let data: WasteResponse | null = null;
  let offline = true;
  try {
    const r = await cachedGet<WasteResponse>('waste', '/waste');
    data = r.data;
    offline = r.offline;
  } catch {
    /* Cache leer */
  }
  const me = currentMe();
  const putouts = me ? overlayPutouts(data?.putouts ?? [], await actionQueue.list(), me.user) : [];
  return { events: data?.events ?? [], putouts, lastSuccess: data?.status.lastSuccess ?? null, offline };
}

function binDots(types: WasteType[]): string {
  return types
    .map((t) => `<span class="bin" style="--bin:${WASTE_TYPES[t].color};--bin-ink:${WASTE_TYPES[t].text}">${esc(WASTE_TYPES[t].label)}</span>`)
    .join(' ');
}

/** Status-Zeile bzw. Knopf „Hab ich rausgestellt" je Tonne eines Termins. */
function putoutControls(date: string, types: WasteType[], putouts: PutoutDTO[]): string {
  const me = currentMe();
  return `<div class="putouts">${types
    .map((t) => {
      const key = wasteKey(date, t);
      const p = putouts.find((x) => x.eventKey === key);
      const label = WASTE_TYPES[t].label;
      if (p) {
        const mayUndo = me && (p.userId === me.user.id || me.user.role === 'admin');
        return `<div class="putout done">✓ ${esc(label)} rausgestellt von ${esc(p.userName)}, ${esc(berlinTime(p.at))}${p.pending ? ' <small>(wird hochgeladen)</small>' : ''}
          ${mayUndo ? `<button type="button" class="linklike" data-undo="${esc(p.id)}" data-pending="${p.pending ? 1 : 0}">Rückgängig</button>` : ''}</div>`;
      }
      return `<button type="button" class="btn putout-btn" data-putout="${esc(key)}">${types.length > 1 ? `${esc(label)}: ` : ''}Hab ich rausgestellt</button>`;
    })
    .join('')}</div>`;
}

/** Banner für die Startseite – Berechnung lokal zur aktuellen Uhrzeit. */
export function wasteBanner(data: Pick<WasteData, 'events' | 'putouts'>, now = Date.now()): string {
  const n = wasteNotice(data.events, now);
  const today = berlinDate(now);
  const parts: string[] = [];
  if (n.tonight.length) {
    const main = WASTE_TYPES[n.tonight[0]];
    parts.push(`<div class="waste-banner" style="--bin:${main.color};--bin-ink:${main.text}">
      <a href="#/muell" class="waste-kicker">Abfuhr morgen, ${esc(dayLabel(n.tomorrowDate))} · alle Termine</a>
      <strong>Heute Abend rausstellen: ${esc(joinLabels(n.tonight))}</strong>
      ${n.tonight.length > 1 ? `<span>${binDots(n.tonight)}</span>` : ''}
      ${putoutControls(n.tomorrowDate, n.tonight, data.putouts)}</div>`);
  }
  if (n.today.length) {
    parts.push(`<div class="waste-banner soft">
      <strong>Heute Abfuhr: ${esc(joinLabels(n.today))}</strong>
      <span class="waste-kicker">Falls noch nicht geschehen: jetzt rausstellen. Leere Tonnen später wieder reinholen.</span>
      ${putoutControls(today, n.today, data.putouts)}</div>`);
  }
  if (n.monday.length && !n.tonight.length) {
    parts.push(`<a href="#/muell" class="waste-banner soft">
      <strong>Montag Abfuhr: ${esc(joinLabels(n.monday))}</strong>
      <span class="waste-kicker">Am Sonntagabend rausstellen – oder heute schon, falls am Wochenende niemand da ist.</span></a>`);
  }
  return parts.join('');
}

/** Knöpfe im Banner verdrahten; `rerender` zeichnet danach neu. */
export function bindPutouts(root: HTMLElement, rerender: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-putout]').forEach((b) =>
    b.addEventListener('click', async () => {
      b.disabled = true;
      await enqueueAction('waste.putout', { eventKey: b.dataset.putout });
      rerender();
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-undo]').forEach((b) =>
    b.addEventListener('click', async () => {
      if (!confirm('Meldung „rausgestellt" zurücknehmen?')) return;
      const id = b.dataset.undo!;
      // Noch nicht hochgeladen? Dann einfach aus der Warteschlange nehmen.
      if (b.dataset.pending === '1' && (await actionQueue.discard(id))) rerender();
      else {
        await enqueueAction('waste.undo', { putoutId: id });
        rerender();
      }
    }),
  );
}

export async function renderWaste(el: HTMLElement): Promise<void> {
  el.innerHTML = '<h1>Müllabfuhr</h1><p class="muted">Lädt …</p>';
  const w = await loadWaste();
  const today = berlinDate(Date.now());
  const list = upcomingWaste(w.events, today, 30);
  const stand = w.lastSuccess ? new Date(w.lastSuccess).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : 'unbekannt';
  el.innerHTML = `
    <h1>Müllabfuhr</h1>
    <p class="muted small">Bledelner Pforte, Lühnde · Stand ${esc(stand)}${w.offline ? ' · offline' : ''}</p>
    <div id="waste-banner">${wasteBanner(w)}</div>
    ${list.length ? `<ul class="waste-list">${list
      .map((e) => {
        const t = WASTE_TYPES[e.type];
        const d = daysBetween(today, e.date);
        const rel = d === 0 ? 'heute' : d === 1 ? 'morgen' : `in ${d} Tagen`;
        return `<li><span class="swatch" style="--bin:${t.color}"></span>
          <span class="waste-date">${esc(dayLabel(e.date))}</span>
          <span class="waste-type">${esc(t.label)}${e.shifted ? ' <em>(verschoben)</em>' : ''}${(() => {
            const p = w.putouts.find((x) => x.eventKey === e.key);
            return p ? `<small class="muted"> ✓ ${esc(p.userName)} ${esc(berlinTime(p.at))}</small>` : '';
          })()}</span>
          <span class="muted small">${rel}</span></li>`;
      })
      .join('')}</ul>` : '<p>Keine Termine im Speicher. Sobald eine Verbindung besteht, wird der Kalender geladen.</p>'}
  `;
  bindPutouts(el, () => void renderWaste(el));
}
